import { useCallback, useEffect, useRef, useState } from "react";
import { Copy, Loader2, QrCode } from "lucide-react";
import { toast } from "sonner";
import { jsPDF } from "jspdf";

const BASE = process.env.REACT_APP_BACKEND_URL;
const SIGN_W = 1240;
const SIGN_H = 1754; // A4 portrait ratio (150dpi)

const loadImage = (src) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => resolve(null);
  img.src = src;
});

function wrapCanvasLines(ctx, text, maxW, maxLines) {
  const words = String(text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let cur = "";
  for (const w of words) {
    const t = (cur + " " + w).trim();
    if (ctx.measureText(t).width <= maxW) { cur = t; continue; }
    if (cur) lines.push(cur);
    cur = w;
    if (lines.length === maxLines - 1) break;
  }
  if (cur && lines.length < maxLines) lines.push(cur);
  // Ellipsize the last line when words remain unplaced.
  const used = lines.join(" ").length;
  if (used < String(text || "").trim().length && lines.length) {
    let last = lines[lines.length - 1] + "…";
    while (ctx.measureText(last).width > maxW && last.length > 1) last = last.slice(0, -2) + "…";
    lines[lines.length - 1] = last;
  }
  return lines;
}

// Client-side print sign: official logo, listing name, canonical QR, property
// code, scan instruction. White background = ink-friendly print output.
async function drawSign(canvas, { name, code, publicUrl, qrUrl }) {
  const ctx = canvas.getContext("2d");
  canvas.width = SIGN_W;
  canvas.height = SIGN_H;
  const [logo, qr] = await Promise.all([loadImage("/brand/logo_light.png"), loadImage(qrUrl)]);
  const cx = SIGN_W / 2;
  const center = (text, y, font, fill) => {
    ctx.font = font; ctx.fillStyle = fill; ctx.textAlign = "center"; ctx.textBaseline = "alphabetic";
    ctx.fillText(text, cx, y);
  };

  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, SIGN_W, SIGN_H);
  ctx.fillStyle = "#E50914";
  ctx.fillRect(0, 0, SIGN_W, 18);
  ctx.fillRect(0, SIGN_H - 18, SIGN_W, 18);

  if (logo) {
    const maxW = 460, maxH = 230;
    const scale = Math.min(maxW / logo.width, maxH / logo.height);
    const w = logo.width * scale, h = logo.height * scale;
    ctx.drawImage(logo, cx - w / 2, 70 + (maxH - h) / 2, w, h);
  } else {
    center("MYREDPARK", 200, "900 84px system-ui, sans-serif", "#E50914");
  }

  // Listing name — up to 2 lines, auto-shrunk, ellipsized; never covers QR/code.
  let size = 72;
  let lines;
  do {
    ctx.font = `800 ${size}px system-ui, sans-serif`;
    lines = wrapCanvasLines(ctx, name, SIGN_W - 160, 2);
    size -= 4;
  } while (lines.some((l) => ctx.measureText(l).width > SIGN_W - 160) && size > 34);
  const nameY = 430;
  lines.forEach((l, i) => center(l, nameY + i * (size + 22), `800 ${size + 4}px system-ui, sans-serif`, "#080B0D"));

  const scanY = nameY + lines.length * (size + 22) + 40;
  center("SCAN TO VIEW PARKING DETAILS", scanY, "600 40px system-ui, sans-serif", "#1F2328");

  const qrSize = 470;
  const qrTop = scanY + 60;
  ctx.strokeStyle = "#E4E4E7"; ctx.lineWidth = 3;
  ctx.strokeRect(cx - qrSize / 2 - 28, qrTop - 28, qrSize + 56, qrSize + 56);
  if (qr) ctx.drawImage(qr, cx - qrSize / 2, qrTop, qrSize, qrSize);
  center(publicUrl.replace(/^https?:\/\//, ""), qrTop + qrSize + 66, "500 30px system-ui, sans-serif", "#52525B");

  const codeLabelY = qrTop + qrSize + 150;
  center("PROPERTY CODE", codeLabelY, "600 30px system-ui, sans-serif", "#71717A");
  center(String(code).split("").join(" "), codeLabelY + 118, "900 110px ui-monospace, monospace", "#E50914");

  center("MYREDPARK.COM", SIGN_H - 78, "800 44px system-ui, sans-serif", "#080B0D");
  return !!qr;
}

// Host-facing property-access card for a marketplace listing (V2 legacy
// integration). QR content is still generated by the canonical server
// endpoint; the printable sign is composed client-side so it can carry the
// live listing name + property code without backend changes.
export default function ListingPropertyAccess({ spot }) {
  const [qrUrl, setQrUrl] = useState(null);
  const [signReady, setSignReady] = useState(false);
  const canvasRef = useRef(null);
  const publicUrl = `${window.location.origin}/p/${spot.property_code}`;

  useEffect(() => {
    let url;
    let live = true;
    setQrUrl(null);
    fetch(`${BASE}/api/spots/${spot.spot_id}/qr.png`, { credentials: "include" })
      .then((r) => (r.ok ? r.blob() : Promise.reject(new Error("qr"))))
      .then((b) => { url = URL.createObjectURL(b); if (live) setQrUrl(url); })
      .catch(() => {});
    return () => { live = false; if (url) URL.revokeObjectURL(url); };
  }, [spot.spot_id]);

  // Re-render the sign whenever the selected listing (or its QR) changes.
  useEffect(() => {
    if (!qrUrl || !canvasRef.current) return;
    let live = true;
    setSignReady(false);
    drawSign(canvasRef.current, { name: spot.title, code: spot.property_code, publicUrl, qrUrl })
      .then((ok) => { if (live) setSignReady(ok); })
      .catch(() => {});
    return () => { live = false; };
  }, [qrUrl, spot.title, spot.property_code, publicUrl]);

  const copy = (text, label) => { navigator.clipboard?.writeText(text)?.catch(() => {}); toast.success(label); };

  const downloadPng = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !signReady) return;
    canvas.toBlob((b) => {
      if (!b) { toast.error("Sign download failed"); return; }
      const url = URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = url;
      a.download = `myredpark-sign-${spot.property_code}.png`;
      a.click();
      URL.revokeObjectURL(url);
    }, "image/png");
  }, [signReady, spot.property_code]);

  const downloadPdf = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !signReady) return;
    const pdf = new jsPDF({ unit: "mm", format: "a4" });
    pdf.addImage(canvas.toDataURL("image/png"), "PNG", 15, 15, 180, 180 * (SIGN_H / SIGN_W));
    pdf.save(`myredpark-sign-${spot.property_code}.pdf`);
  }, [signReady, spot.property_code]);

  return (
    <div className="card-dark p-6" data-testid="listing-property-access">
      <p className="meta-label">Property code &amp; QR</p>
      <div className="mt-4 flex flex-col items-start gap-5 sm:flex-row sm:items-center sm:gap-6">
        <div className="text-center">
          <p className="font-mono font-extrabold text-4xl tracking-[0.2em] text-[#FF3B45] break-all" data-testid="listing-property-code">{spot.property_code}</p>
          <p className="text-[9px] uppercase tracking-widest text-zinc-600 mt-1">4-digit code</p>
        </div>
        <div className="w-28 h-28 rounded-xl bg-white p-2 flex items-center justify-center shrink-0" data-testid="listing-qr-image">
          {qrUrl ? <img src={qrUrl} alt={`QR code linking to ${publicUrl}`} className="w-full h-full" /> : <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />}
        </div>
        <div className="min-w-0 flex-1 space-y-2.5">
          <div className="flex items-center gap-2 min-w-0">
            <code className="text-xs text-zinc-300 bg-white/[0.04] rounded-lg px-2.5 py-1.5 min-w-0 break-all" data-testid="listing-public-url">{publicUrl}</code>
            <button onClick={() => copy(publicUrl, "Link copied")} className="p-1.5 text-zinc-500 hover:text-white transition-colors" aria-label="Copy link" data-testid="listing-copy-url">
              <Copy className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            <button onClick={() => copy(spot.property_code, "Code copied")} className="btn-secondary !py-2 !px-3 text-xs" data-testid="listing-copy-code">Copy Code</button>
            {qrUrl && (
              <a href={qrUrl} download={`myredpark-property-${spot.property_code}.png`}
                 className="btn-secondary !py-2 !px-3 text-xs inline-flex items-center gap-1.5" data-testid="listing-qr-download">
                <QrCode className="w-3.5 h-3.5" /> Download QR
              </a>
            )}
            <button onClick={downloadPdf} disabled={!signReady} className="btn-secondary !py-2 !px-3 text-xs disabled:opacity-40" data-testid="listing-sign-pdf">Download sign (PDF)</button>
            <button onClick={downloadPng} disabled={!signReady} className="btn-secondary !py-2 !px-3 text-xs disabled:opacity-40" data-testid="listing-sign-png">Sign (PNG)</button>
          </div>
          <p className="text-[11px] text-zinc-600">Drivers scan this QR or enter the code on the homepage to park at this listing. The code never changes.</p>
        </div>
      </div>

      {/* Printable sign preview — live render of exactly what downloads. */}
      <div className="mt-6 pt-6 border-t border-[#27272A]">
        <p className="meta-label">Printable sign preview</p>
        <p className="mt-1 text-[11px] text-zinc-600">Shows this listing's name and property code. Print at A4 for best results.</p>
        <div className="mt-4 relative max-w-sm rounded-xl overflow-hidden border border-[#27272A] bg-white" data-testid="listing-sign-preview">
          <canvas ref={canvasRef} className="w-full h-auto block" data-testid="listing-sign-canvas" />
          {!signReady && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/60">
              <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
